import { userContext } from "@frontend/contexts/UserContext";
import { setSentryUser } from "@frontend/instrumentation";
import { setActiveTeamIdForRequests } from "@frontend/utils/active_team_header.client";
import { authClient } from "@frontend/utils/auth.client";
import { orpcFetch } from "@frontend/utils/orpc.client";
import { detectUserTimezone } from "@frontend/utils/timezone.utils";
import type { LoaderFunctionArgs } from "react-router";
import { redirect } from "react-router";
import { toast } from "react-toastify";

/**
 * Auth loader for protected routes (online-only: no cached-session fallback).
 * Fetches the session, seeds the workspace header, sets the React Router
 * context, and redirects to sign-in when there is no session.
 */
export async function authLoader({ context }: LoaderFunctionArgs) {
	try {
		const { data: session, error } = await authClient.getSession();
		if (error || !session) throw redirect("/auth");

		// Timezone Detection and Auto-Update
		try {
			const detectedTimezone = await detectUserTimezone();
			if (detectedTimezone && detectedTimezone !== session.user.timezone) {
				toast.info(`Timezone change detected. Updating timezone to match your current location.`, {
					position: "top-center",
					autoClose: 1000,
				});

				await authClient.updateUser({ timezone: detectedTimezone });
				session.user.timezone = detectedTimezone;

				toast.success(`Your timezone has been updated to match your current location`, {
					position: "top-center",
					autoClose: 3000,
				});
			}
		} catch (timezoneError) {
			console.error(timezoneError);
			toast.error("Timezone detection/update failed.");
		}

		// Seed the `x-team-id` header cache BEFORE any component mounts: the RPC
		// link waits for this seed, and team-scoped endpoints reject a request
		// without the header ("Active team id mismatch").
		setActiveTeamIdForRequests(session.user.activeTeamAppId ?? null);

		// Self-heal for a stale better-auth cookie cache (`auth.config.ts`
		// `session.cookieCache.maxAge = 5min`): a cookie signed before
		// `users.activeTeamAppId` was set reports `null` for up to 5 minutes.
		// `getDefaultTeam` is session-only (no `x-team-id` needed), joins the
		// hidden workspace if necessary and returns it.
		if (!session.user.activeTeamAppId) {
			try {
				const defaultTeam = await orpcFetch.teams.getDefaultTeam();
				session.user.activeTeamAppId = defaultTeam.id;
				setActiveTeamIdForRequests(defaultTeam.id);
			} catch (err) {
				console.warn("[authLoader] getDefaultTeam fallback failed", err);
			}
		}

		const sessionInfo = {
			hasSession: true,
			user: session.user,
			isRegistered: true, // better-auth handles registration
		};

		setSentryUser({
			email: session.user.email,
			username: session.user.name,
			id: session.user.id,
		});

		context.set(userContext, sessionInfo);
		return sessionInfo;
	} catch (error) {
		if (error instanceof Response) throw error; // Re-throw redirects
		console.error("Auth loader error:", error);
		throw redirect("/auth");
	}
}
